import BaseRepository from "~/api/BaseRepository";
import {formatDate} from "#shared/utils/date";

export interface InvoiceLineItem {
  title: string;
  quantity: number;
  unitPrice: number;
}

/** Mirrors `VatRegime` in the API (`entities::invoice`). */
export type VatRegime = 'standard' | 'outside_eu';

/** Mirrors `CreatedInvoiceResponse` in the API (`rest::v1::invoices::edit`). */
export interface CreatedInvoiceResponse {
  invoice_id: string;
}

export interface CreateInvoicePayload {
  missionId: string;
  periodStart: Date;
  periodEnd: Date;
  dueDate: Date;
  items: InvoiceLineItem[];
  tenantId?: string;
  vatRegime: VatRegime;
}

export interface CommissionInvoice {
  invoice_id: string;
  mission_id: string;
  created_at: string;
  amount_ht: number;
  amount_ttc: number;
  status: string;
  number: number;
  invoice_number?: string;
}

export interface Invoice {
  invoice_id: string;
  mission_id: string;
  mission_name?: string;
  status: string;
  period_start: string;
  period_end: string;
  created_at: string;
  updated_at: string;
  due_date: string;
  lines: InvoiceLineItem[];
  invoice_number?: string;
  amount_ht?: number;
  amount_ttc?: number;
  emitter?: string;
  number?: number;
  stripe_payment_intent_id?: string;
  client_name?: string;
  siret?: string;
  tva_number?: string;
  address?: string;
  items?: InvoiceItemDetail[];
  paid_date?: string;
  freelance_name?: string;
  freelance_siret?: string;
  freelance_address?: string;
  parent_invoice_id?: string;
  /** Only on the detail endpoint; empty unless paid and viewed by the emitting company. */
  commission_invoices?: CommissionInvoice[];
}

export interface InvoiceItemDetail {
  title: string;
  quantity: number;
  unit_price: number;
  vat_rate: number;
}

export interface ListInvoicesResponse {
  invoices: Invoice[];
  total: number;
}

interface listInvoicesParams {
  missionId: string;
}

interface ListUserInvoicesParams {
  userId: string;
  limit?: number;
  offset?: number;
}

interface ListCompanyInvoicesParams {
  companyId: string;
  limit?: number;
  offset?: number;
}

interface ListReceivedInvoicesParams {
  companyId: string;
  limit?: number;
  offset?: number;
  missionId?: string;
  expertId?: string;
}

export interface ExpertDTO {
  user_id: string;
  first_name: string;
  last_name: string;
}

export interface ReceivedInvoice {
  invoice_id: string;
  mission_id: string;
  mission_name: string;
  expert: ExpertDTO;
  created_at: string;
  amount_ht: number;
  amount_ttc: number;
  status: string;
  invoice_number?: string;
  parent_invoice_id?: string;
}

export interface ListReceivedInvoicesResponse {
  invoices: ReceivedInvoice[];
  total: number;
}

export interface ListExpertsResponse {
  experts: ExpertDTO[];
}

export const InvoiceRepository = (client) => ({
  ...BaseRepository(client)('/invoices'),

  listInvoices: (params: listInvoicesParams) => {
    return client(`/v1/missions/${params.missionId}/invoices`, {
      method: 'GET',
    }) as Promise<ListInvoicesResponse>;
  },
  
  listByEmployee: (params: ListUserInvoicesParams) => {
    const queryParams = new URLSearchParams();
    
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    
    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }
    
    return client(`/v1/user/${params.userId}/invoices?${queryParams.toString()}`, {
      method: 'GET',
    }) as Promise<ListInvoicesResponse>;
  },
  
  listByCompany: (params: ListCompanyInvoicesParams) => {
    const queryParams = new URLSearchParams();

    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }

    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }

    return client(`/v1/companies/${params.companyId}/invoices?${queryParams.toString()}`, {
      method: 'GET',
    }) as Promise<ListInvoicesResponse>;
  },

  listReceivedByCompany: (params: ListReceivedInvoicesParams) => {
    const queryParams = new URLSearchParams();

    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }

    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }

    if (params.missionId !== undefined) {
      queryParams.append('mission_id', params.missionId);
    }

    if (params.expertId !== undefined) {
      queryParams.append('expert_id', params.expertId);
    }

    return client(`/v1/companies/${params.companyId}/received-invoices?${queryParams.toString()}`, {
      method: 'GET',
    }) as Promise<ListReceivedInvoicesResponse>;
  },

  listInvoiceExperts: (companyId: string) => {
    return client(`/v1/companies/${companyId}/received-invoices/experts`, {
      method: 'GET',
    }) as Promise<ListExpertsResponse>;
  },
  
  getInvoice: (invoiceId: string) => {
    return client(`/v1/invoices/${invoiceId}`, {
      method: 'GET',
    }) as Promise<Invoice>;
  },
  
  getInvoiceDetail: (invoiceId: string) => {
    return client(`/v1/invoices/${invoiceId}`, {
      method: 'GET',
    }) as Promise<Invoice>;
  },
  
  previewInvoice: (invoiceId: string, missionId: string) => {
    return client(`/v1/missions/${missionId}/invoices/${invoiceId}/preview`, {
      method: 'GET',
    });
  },
  
  sendInvoice: (invoiceId: string, missionId: string) => {
    return client(`/v1/missions/${missionId}/invoices/${invoiceId}/send`, {
      method: 'POST',
    });
  },
  
  deleteInvoice: (invoiceId: string, missionId: string) => {
    return client(`/v1/missions/${missionId}/invoices/${invoiceId}`, {
      method: 'DELETE',
    });
  },
  
  updateInvoice: (invoiceId: string, missionId: string, payload: Partial<CreateInvoicePayload>) => {
    // To compensate for timezone offset, we add one day to dates
    // when they are sent for update
    const formatDateWithOffset = (date: Date): string => {
      // Create a new date with one more day to compensate for UTC offset
      const offsetDate = new Date(date);
      offsetDate.setDate(offsetDate.getDate() + 1);

      const year = offsetDate.getFullYear();
      const month = (offsetDate.getMonth() + 1).toString().padStart(2, '0');
      const day = offsetDate.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const formData = new URLSearchParams();
    
    if (payload.periodStart) {
      formData.append('period_start', formatDateWithOffset(payload.periodStart));
    }
    
    if (payload.periodEnd) {
      formData.append('period_end', formatDateWithOffset(payload.periodEnd));
    }
    
    if (payload.dueDate) {
      formData.append('due_date', formatDateWithOffset(payload.dueDate));
    }
    
    if (payload.items && payload.items.length > 0) {
      payload.items.forEach((item) => {
        formData.append('items.titles[]', item.title);
        formData.append('items.quantities[]', item.quantity.toString());
        formData.append('items.unit_prices[]', item.unitPrice.toString());
      });
    }

    return client(`/v1/missions/${missionId}/invoices/${invoiceId}`, {
      method: 'PATCH',
      body: formData.toString(),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      }
    });
  },
  
  create: (payload: CreateInvoicePayload, options: { skipGlobalErrorAlert?: boolean } = {}) => {
    const periodStart = formatDate(payload.periodStart);
    const periodEnd = formatDate(payload.periodEnd);

    const formData = new URLSearchParams();
    formData.append('period_start', periodStart);
    formData.append('period_end', periodEnd);

    if (payload.dueDate) {
      formData.append('due_date', formatDate(payload.dueDate));
    }

    payload.items.forEach((item) => {
      formData.append('items.titles[]', item.title);
      formData.append('items.quantities[]', item.quantity.toString());
      formData.append('items.unit_prices[]', item.unitPrice.toString());
    });

    // Add a valid tenant_id (at least 2 characters)
    formData.append('tenant_id', payload.tenantId || 'PROD');
    formData.append('vat_regime', payload.vatRegime);

    return client(`/v1/invoices?mission=${payload.missionId}`, {
      method: 'POST',
      body: formData.toString(),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      // The page translates the VAT regime refusals itself
      ...options,
    }) as Promise<CreatedInvoiceResponse>;
  },
});